<template>
  <div>
    <v-container
      v-if="variables.loading"
      class="d-flex justify-center align-center my-16"
    >
      <v-progress-circular
        indeterminate
        size="75"
      />
    </v-container>
    <div v-else>
      <div
        class="mb-4"
      >
        <v-row align="center">
          <v-col>
            <div class="subtitle-2 grey--text text--grey-100">
              {{ $t("card_title.team_stats") }}
            </div>
          </v-col>
          <v-col
            v-if="dashboard === DashboardType.EVENT_PERFORMANCE || !dashboard"
            cols="auto"
          >
            <v-row no-gutters>
              <v-col
                v-for="category in categories"
                :key="category.value"
                cols="auto pa-0"
              >
                <v-btn
                  class="ml-2"
                  :class="view === category.value ? 'text-none' : 'text-none v-btn--outlined'"
                  :color="view === category.value ? 'primary' : undefined"
                  :text="view !== category.value"
                  @click="view = category.value"
                >
                  {{ category.text }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="(variableTemplate, index) in teamVariablesTemplate[view]"
            :key="variableTemplate.id"
          >
            <v-card class="fill-height">
              <v-card-text class="pa-4">
                <v-row
                  no-gutters
                  align="center"
                >
                  <v-col>
                    <v-select
                      v-model="variableTemplate.variable"
                      :items="teamVariables.filter(v => (variableTemplate.variable && variableTemplate.variable.id === v.id) || !teamVariablesTemplate[view].some(templateVar => templateVar.variable && templateVar.variable.id === v.id))"
                      item-text="text"
                      item-value="text"
                      :placeholder="$t('dropdown.select.placeholder')"
                      dense
                      hide-details
                      return-object
                      @change="emitInput"
                    />
                  </v-col>
                  <v-col cols="auto">
                    <v-btn
                      icon
                      small
                      right
                      @click="unselectTeamVariable(index)"
                    >
                      <v-icon
                        small
                      >
                        fal fa-times
                      </v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
                <div
                  class="text-h4 my-3 grey--text text--darken-2"
                >
                  <div v-if="variableTemplate.variable">
                    {{ variableTemplate.value }}
                  </div>
                  <div v-else>
                    N/A
                  </div>
                </div>
                <v-progress-linear
                  class
                  :height="8"
                  rounded
                  background-color="grey"
                  :background-opacity=".2"
                  :value="variableTemplate.variable ? (variableTemplate.value / 1000) * 100 : 0"
                  :color="getColorFromValue(variableTemplate.value, 1000)"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <div v-if="!dashboard">
          <v-row
            align="center"
            class="mt-6"
          >
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100">
                {{ $t("card_title.table_columns") }}
              </div>
            </v-col>
            <v-col
              cols="auto"
              class="pl-2"
            >
              <variable-selector
                :key="view"
                :variables="variables.data"
                :category="view"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-data-table
                :headers="tableHeaders"
                :items="items"
                dense
                hide-default-footer
              >
                <template v-slot:item="{ item }">
                  <tr
                    class="clickable"
                    @click="$emit('select:player', item)"
                  >
                    <td class="text-no-wrap">
                      {{ item.name }}
                    </td>
                    <td
                      v-for="tableHeader in tableHeaders.slice(1)"
                      :key="tableHeader.id"
                    >
                      <table-bar-value
                        :value="item[tableHeader.value]"
                        :max="100"
                        :color="getColorFromValue(item[tableHeader.value], 100)"
                      >
                        {{ item[tableHeader.value] }}
                      </table-bar-value>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { OrganisationApiService } from '@/data/services/OrganisationApiService'
import { orderBy } from 'lodash'
import { CoachApiService } from '@/data/services'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import TableBarValue from '@/components/helpers/TableBarValue.vue'
import { getColorForValue } from '@/utils/helpers'
import { DashboardType } from '@/data/models/Dashboard'

export default {
  name: 'PerformanceVariableSelection',
  components: { TableBarValue, VariableSelector },
  props: {
    coach: {
      type: Object,
      default: null
    },
    dashboard: {
      type: String,
      default: null
    },
    defaultView: {
      type: String,
      default: 'main'
    }
  },
  data: () => ({
    variables: {
      data: null,
      loading: false
    },
    playerView: false,
    view: 'SUMMARY',
    search: '',
    submitting: false,
    teamVariablesTemplate: {
      GPS: [
        { variable: null, order: 1 },
        { variable: null, order: 2 },
        { variable: null, order: 3 },
        { variable: null, order: 4 },
        { variable: null, order: 5 }
      ],
      HR: [
        { variable: null, order: 1 },
        { variable: null, order: 2 },
        { variable: null, order: 3 },
        { variable: null, order: 4 },
        { variable: null, order: 5 }
      ],
      RR: [
        { variable: null, order: 1 },
        { variable: null, order: 2 },
        { variable: null, order: 3 },
        { variable: null, order: 4 },
        { variable: null, order: 5 }
      ],
      RPE: [
        { variable: null, order: 1 },
        { variable: null, order: 2 },
        { variable: null, order: 3 },
        { variable: null, order: 4 },
        { variable: null, order: 5 }
      ],
      SUMMARY: [
        { variable: null, order: 1 },
        { variable: null, order: 2 },
        { variable: null, order: 3 },
        { variable: null, order: 4 },
        { variable: null, order: 5 }
      ]
    }
  }),
  computed: {
    DashboardType() {
      return DashboardType
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    teamVariables() {
      if (!this.variables.data) return []

      return this.variables.data.team[this.view]
    },
    items() {
      return ['John Snow', 'Kenny Adams'].map(name => ({
        name,
        ...Object.fromEntries(this.tableHeaders.map(header => [header.value, Math.floor(Math.random() * 100)]))
      }))
    },
    tableHeaders() {
      if (this.view === 'GPS') {
        return this.tableHeadersGPS
      } else if (this.view === 'HR') {
        return this.tableHeadersHR
      } else if (this.view === 'RR') {
        return this.tableHeadersRR
      } else if (this.view === 'RPE') {
        return this.tableHeadersRPE
      } else {
        return this.tableHeadersSUM
      }
    },
    tableHeadersSUM() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.SUMMARY.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersRPE() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.RPE.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersGPS() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.GPS.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersHR() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.HR.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    tableHeadersRR() {
      return this.variables.data ? [{ value: 'last_name', text: this.$t('table_headers.name') }].concat(orderBy(this.variables.data.player.RR.filter(tableHeader => tableHeader.selected), ['order'])) : []
    },
    categories() {
      return [
        {
          text: this.$t('performance.event.summary'),
          value: 'SUMMARY'
        },
        {
          text: this.$t('performance.event.rpe'),
          value: 'RPE'
        },
        {
          text: this.$t('performance.event.gps'),
          value: 'GPS'
        },
        {
          text: this.$t('performance.event.hr'),
          value: 'HR'
        },
        {
          text: this.$t('performance.event.rr'),
          value: 'RR'
        }
      ]
    }
  },
  watch: {
    defaultView() {
      this.view = this.defaultView
    },
    variables: {
      deep: true,
      handler: function () {
        this.emitInput()
      }
    }
  },
  created() {
    this.fetchVariables()
    this.view = this.defaultView
  },
  methods: {
    async fetchVariables() {
      this.variables.loading = true

      try {
        if (this.coach) {
          this.variables.data = await CoachApiService.getVariables(this.coach.id, { dashboard: this.dashboard })
        } else {
          this.variables.data = await OrganisationApiService.getVariables(this.user.organisation_id)
        }

        Object.keys(this.variables.data).forEach(group => {
          Object.keys(this.variables.data[group]).forEach(category => {
            this.variables.data[group][category] = this.variables.data[group][category].map(v => {
              return {
                ...v,
                text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
                long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
              }
            })
          })
        })

        this.initTeamVariables()
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    unselectTeamVariable(index) {
      for (let i = index; i < this.teamVariablesTemplate[this.view].length - 1; i++) {
        this.teamVariablesTemplate[this.view][i].variable = this.teamVariablesTemplate[this.view][i + 1].variable
      }
      this.teamVariablesTemplate[this.view][4].variable = null
      this.emitInput()
    },
    emitInput() {
      if (!this.variables.data) return

      const data = {
        team: Object.entries(this.teamVariablesTemplate)
          .filter(([key]) => key !== 'SUMMARY')
          .flatMap(([, values]) => values)
          .map(variable => ({
            id: variable.variable?.id,
            order: variable.order
          })).filter(variable => variable.id),

        player: Object.entries(this.variables.data.player)
          .filter(([key]) => key !== 'SUMMARY')
          .flatMap(([, values]) => values)
          .filter(variable => variable.selected)
          .map(variable => ({
            id: variable.id,
            order: variable.order
          })).filter(v => v.order),

        summary_team: this.teamVariablesTemplate.SUMMARY.map(variable => ({
          id: variable.variable?.id || null,
          order: variable.order
        })).filter(variable => variable.id),

        summary_player: this.variables.data.player.SUMMARY.filter(variable => variable.selected).map(variable => ({
          id: variable.id,
          order: variable.order
        })).filter(v => v.order)
      }

      this.$emit('input', data)
    },
    getColorFromValue(value, max, reverse = false) {
      return getColorForValue(value, max, reverse)
    },
    initTeamVariables() {
      if (!this.variables.data) return

      Object.keys(this.teamVariablesTemplate).forEach(category => {
        for (const template of this.teamVariablesTemplate[category]) {
          template.variable = this.variables.data.team[category]?.find(v => v.order === template.order && v.selected) || null
          template.value = Math.round(Math.floor(Math.random() * 100000) * 1.11) / 100
        }
      })
    }
  }
}
</script>

<style scoped>

</style>
